import { useEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * 社内サービス横断リンクの waffle ランチャー
 * (lightmedia-admin-theme の .lm-app-* コンポーネントを使用)。
 *
 * 各サービスは別々の会員基盤 (別認証) でセッションを共有しないため、
 * 横断では他サービスの認証状態・権限は判定できない。よって権限による
 * 出し分けはせず、静的なリンク集とする (判定は遷移先の各サービスに委ねる)。
 *
 * 各サービスは全ユーザー共通の固定 URL とする。AI Creative は自社サービス
 * showhin の org-admin レポート画面 (https://showhin.lightmedia.jp/org-admin/reports)
 * を指す。
 */
type AppLink = { name: string; href: string; color: string; mono: string };

const APPS: AppLink[] = [
  // Dashboard は全社の入口なので常に先頭 (左上)。タイル色だけテーマ色 (#2563eb) を
  // 使わずスレートにしているのは、Light CMS が同じブルーで並ぶと見分けが付かないため
  { name: 'Dashboard', href: 'https://dashboard.lightmedia.jp', color: '#475569', mono: 'Dash' },
  { name: 'AI Creative', href: 'https://showhin.lightmedia.jp/org-admin/reports', color: '#4f46e5', mono: 'AI' },
  { name: 'Light MA', href: 'https://ma.lightmedia.jp/admin/', color: '#0d9488', mono: 'MA' },
  { name: 'Light CMS', href: 'https://cms.lightmedia.jp', color: '#2563eb', mono: 'CMS' },
  { name: 'Ad Agent', href: 'https://ad.lightmedia.jp', color: '#7c3aed', mono: 'Ad' },
];

export default function AppLauncher() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // CSS はスタイルのみ提供する契約。開閉・外側クリック検知は React 側で行う。
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: MouseEvent) {
      if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  return (
    <div className={open ? 'lm-menu-anchor is-open' : 'lm-menu-anchor'} ref={anchorRef}>
      <button
        type="button"
        className="lm-icon-btn"
        aria-label="サービス"
        aria-haspopup="true"
        title="サービス"
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
          {[1, 7, 13].flatMap((y) =>
            [1, 7, 13].map((x) => <rect key={`${x}-${y}`} x={x} y={y} width="4" height="4" rx="1" />),
          )}
        </svg>
      </button>
      <div className="lm-app-menu" role="menu">
        <div className="lm-app-grid">
          {APPS.map((a) => (
            <a
              key={a.name}
              className="lm-app-item"
              href={a.href}
              role="menuitem"
              style={{ '--lm-app-color': a.color } as CSSProperties}
            >
              <span className="lm-app-icon">{a.mono}</span>
              <span className="lm-app-name">{a.name}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
